import {
  MorphemeTilePresentationProvider,
  useMorphemeTilePresentation,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { stringLength } from "@-/com.npmjs.string-length";
import { Platform, Pressable, StyleSheet, View } from "@-/com.npmjs.react-native";
import { type ReactNode, useCallback, useMemo } from "react";
import { styled } from "styled-components/native";
import { copyToClipboard } from "@-/dev.improve.haniverse.lib.clipboard";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@-/dev.improve.haniverse.components.ui.context-menu";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { isCardCompleted } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.file.completion";
import type { MorphemeGlyphTileStage } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import {
  MORPHEME_TILE_DELAY_LONG_PRESS_MS,
  morphemeGlyphTileVariants,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.variants";
import { cn } from "@-/com.npmjs.cn";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import {
  CARD_PREVIEW_TILE_MORPHEME_BOX_PADDING,
  type MorphemeDetailsTilePinyinEntry,
  MorphemeDetailsTileGlyph,
  MorphemeDetailsTilePinyinRow,
  getMorphemeDetailsTilePinyinBelowGlyphMargin,
  getMorphemeDetailsTileMultiCharacterGlyphStyle,
  getMorphemeDetailsTileMultiCharacterMorphemeBoxHeight,
  getMorphemeDetailsTileMultiCharacterMorphemeBoxWidth,
  getMorphemeDetailsTilePinyinSizing,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.details._section.morpheme";
import { MorphemeDetailsTileGlossSection } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.details._section.gloss";
import {
  getMorphemeDetailsTileStatsSectionGate,
  MorphemeDetailsTileStatsSection,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.details._section.stats";
import { getMorphemeStatsLabels } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.rank";
import {
  splitToneNumbersPinyinSyllables,
  toDiacriticPinyin,
  toDiacriticPinyinSyllables,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { toneForPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.transcription";
import { pinyinForGrapheme } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph";
import { hasMinLength } from "@-/com.npmjs.ts-array-length";

/**
 * Query container that sizes its children's `cqi` units off the card's own width.
 * The card already declares itself as a proportional widget via the `size` prop;
 * `cqi`-based labels (stats / gloss) keep that contract: text scales with the
 * tile, not with the viewport. See CardDetailsTileUi for the parallel pattern.
 */
const MorphemeDetailTileQueryContainer = styled(View)`
  flex: 1;
  container-type: inline-size;
`;
const NO_STUDY_PINYIN: MorphemeDetailsTileSlice["pinyinList"] = [];

export type MorphemeDetailsTileUiProps = {
  className?: string;
  graphemeCard: MorphemeDetailsTileSlice;
  isEditing?: boolean;
  isLoading?: boolean;
  /** Whether this tile is part of the app-wide selection. */
  isSelected?: boolean;
  onGraphemePress?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  onLongPress?: () => void;
  onPress?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  onRemoveCard?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  size: number;
  /**
   * The grapheme's SRS tile stage, resolved by the caller's single batched
   * `useMorphemeListStagesQuery` and passed in. Mandatory so this tile fires NO
   * per-tile stage query — a grid of these otherwise mounted one stage `useQuery`
   * + one change subscription PER tile, and a multi-row reveal write then
   * restaged every mounted grapheme's key individually (the query storm this
   * batching removes).
   */
  stage: MorphemeGlyphTileStage;
};

function MorphemeDetailsTileUiContent({
  className,
  graphemeCard,
  isEditing = false,
  isLoading,
  isSelected = false,
  onGraphemePress,
  onLongPress,
  onPress,
  onRemoveCard,
  size,
  stage: morphemeStage,
}: MorphemeDetailsTileUiProps) {
  const {
    fadeMissingMnemonicEnabled,
    getGraphemePrevalence,
    isToneColorsEnabled,
    toneColors,
    user,
  } = useMorphemeTilePresentation();
  const { levelLabel, rankLabel } = getMorphemeStatsLabels(graphemeCard, user);
  const statsSectionGate = getMorphemeDetailsTileStatsSectionGate({ levelLabel, rankLabel });
  const isCharacter = getGraphemePrevalence(graphemeCard.front).asCharacter !== "obscure";
  const studyPinyinList = isCharacter ? graphemeCard.pinyinList : NO_STUDY_PINYIN;
  const mainPinyin = isCharacter ? (pinyinForGrapheme(graphemeCard.front) ?? "") : null;
  const isSingleCharacter = stringLength(graphemeCard.front) === 1;
  const primaryToneNumbersPinyin = useMemo(() => {
    if (isSingleCharacter) {
      return mainPinyin || studyPinyinList[0]?.toneNumbersPinyin || null;
    }
    return studyPinyinList[0]?.toneNumbersPinyin ?? null;
  }, [mainPinyin, isSingleCharacter, studyPinyinList]);
  const pinyinEntries = useMemo<readonly MorphemeDetailsTilePinyinEntry[]>(
    () =>
      studyPinyinList.map(({ toneNumbersPinyin }) => {
        const syllableToneNumbersList = splitToneNumbersPinyinSyllables(toneNumbersPinyin);
        const diacriticSyllables = toDiacriticPinyinSyllables(toneNumbersPinyin);
        if (!hasMinLength(syllableToneNumbersList, 2)) {
          const pinyin = diacriticSyllables[0] ?? toDiacriticPinyin(toneNumbersPinyin);
          if (!isToneColorsEnabled) {
            return { color: null, pinyin, toneNumbersPinyin };
          }
          const tone = toneForPinyin(pinyin);
          return {
            color: tone === null ? null : toneColors[tone],
            pinyin,
            toneNumbersPinyin,
          };
        }
        const syllables = diacriticSyllables.map((pinyin, index) => {
          if (!isToneColorsEnabled) {
            return { color: null, pinyin };
          }
          const tone = toneForPinyin(syllableToneNumbersList[index] ?? pinyin);
          return { color: tone === null ? null : toneColors[tone], pinyin };
        });
        return {
          color: null,
          pinyin: toDiacriticPinyin(toneNumbersPinyin),
          syllables,
          toneNumbersPinyin,
        };
      }),
    [isToneColorsEnabled, studyPinyinList, toneColors],
  );
  const morphemeBoxWidth = getMorphemeDetailsTileMultiCharacterMorphemeBoxWidth({
    // The square's one-pixel borders sit outside its content width.
    tileSize: size - 2,
  });
  const pinyinSizing = getMorphemeDetailsTilePinyinSizing({ tileSize: size });
  const morphemeBoxHeight = getMorphemeDetailsTileMultiCharacterMorphemeBoxHeight({
    tileSize: size,
  });
  const glyphAvailableHeight =
    morphemeBoxHeight - CARD_PREVIEW_TILE_MORPHEME_BOX_PADDING * 2 - pinyinSizing.lineHeight;
  const glyphStyle = getMorphemeDetailsTileMultiCharacterGlyphStyle({
    availableHeight: glyphAvailableHeight,
    availableWidth: morphemeBoxWidth,
    characterCount: stringLength(graphemeCard.front),
  });
  // The maximum is a sizing constraint, not a fixed spacer above the pinyin.
  const glyphRowHeight = glyphStyle.lineHeight + CARD_PREVIEW_TILE_MORPHEME_BOX_PADDING * 2;
  const pinyinBelowGlyphMargin = Math.floor(
    getMorphemeDetailsTilePinyinBelowGlyphMargin({
      glyphAvailableHeight: glyphRowHeight,
    }) / 2,
  );
  const tileVariantClassName = morphemeGlyphTileVariants({
    completion: isLoading ? "loading" : "none",
    stage: morphemeStage,
  });
  const fadeMissingMnemonicClassName =
    fadeMissingMnemonicEnabled && !isCardCompleted(graphemeCard) ? "opacity-25" : undefined;

  const handlePress = useCallback(() => {
    if (isEditing || isLoading) {
      return;
    }
    onGraphemePress?.(graphemeCard);
    onPress?.(graphemeCard);
  }, [graphemeCard, isEditing, isLoading, onGraphemePress, onPress]);

  const withWebContextMenu = (children: ReactNode) => {
    if (Platform.OS !== "web") {
      return children;
    }
    // ContextMenu's root view shrinks to content on web unless it is explicitly
    // stretched inside the square tile wrapper from MorphemeDetailsTileList.
    return (
      <View style={StyleSheet.absoluteFill}>
        <ContextMenu style={StyleSheet.absoluteFill}>
          <ContextMenuTrigger asChild>
            <View style={StyleSheet.absoluteFill}>{children}</View>
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem
              onPress={() => {
                void copyToClipboard(graphemeCard.front, { label: graphemeCard.front });
              }}
            >
              <Text>Copy {graphemeCard.front}</Text>
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      </View>
    );
  };

  const MorphemeQueryContainer = Platform.OS === "web" ? MorphemeDetailTileQueryContainer : View;
  return withWebContextMenu(
    <MorphemeQueryContainer className="flex-1">
      <Button
        accessibilityLabel={`${graphemeCard.front}, morpheme details`}
        accessibilityState={{ selected: isSelected }}
        className={cn(
          tileVariantClassName,
          // `size="fill"` skips `default`'s `h-10` / `sm:h-9`. `flex-1` grows to
          // the square tile wrapper (required on web inside the context-menu shell).
          // Override glyphs-grid layout from `morphemeGlyphTileVariants` base.
          "aspect-auto min-h-0 w-full flex-1 flex-col items-stretch justify-start gap-0 rounded-xs p-0",
          fadeMissingMnemonicClassName,
          isSelected && "border-1 border-solid border-primary/40",
          className,
        )}
        delayLongPress={MORPHEME_TILE_DELAY_LONG_PRESS_MS}
        isLoading={isLoading}
        onLongPress={isEditing ? undefined : onLongPress}
        onPress={handlePress}
        size="fill"
        variant="ghost"
      >
        <View
          className="shrink-0 flex-col items-start justify-start self-stretch"
          style={{
            padding: CARD_PREVIEW_TILE_MORPHEME_BOX_PADDING,
          }}
        >
          <View
            className="w-full shrink-0 flex-col items-start justify-start"
            style={{ height: glyphRowHeight }}
          >
            <MorphemeDetailsTileGlyph
              availableHeight={glyphAvailableHeight}
              availableWidth={morphemeBoxWidth}
              graphemeCard={graphemeCard}
              primaryToneNumbersPinyin={primaryToneNumbersPinyin}
            />
          </View>
          <View
            className="w-full shrink-0 flex-row items-start self-stretch"
            style={{
              height: pinyinSizing.lineHeight,
              marginTop: -pinyinBelowGlyphMargin,
            }}
          >
            <MorphemeDetailsTilePinyinRow
              entries={pinyinEntries}
              fontSize={pinyinSizing.fontSize}
              lineHeight={pinyinSizing.lineHeight}
              primaryToneNumbersPinyin={primaryToneNumbersPinyin}
            />
          </View>
        </View>
        <View className="min-h-0 min-w-0 flex-1 flex-col self-stretch">
          <MorphemeDetailsTileGlossSection graphemeCard={graphemeCard} size={size} />
        </View>
        {statsSectionGate !== null && (
          <MorphemeDetailsTileStatsSection gate={statsSectionGate} size={size} />
        )}
        {isEditing && onRemoveCard ? (
          <Pressable
            className="absolute top-1.5 right-1.5 size-6 items-center justify-center rounded-full border border-border bg-background"
            hitSlop={8}
            onPress={() => {
              onRemoveCard(graphemeCard);
            }}
          >
            <Text className="text-xs font-semibold text-foreground">X</Text>
          </Pressable>
        ) : null}
      </Button>
    </MorphemeQueryContainer>,
  );
}

export function MorphemeDetailsTileUi(props: MorphemeDetailsTileUiProps) {
  return (
    <MorphemeTilePresentationProvider>
      <MorphemeDetailsTileUiContent {...props} />
    </MorphemeTilePresentationProvider>
  );
}
